import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * VendorAuthModal
 *
 * Phone → OTP two-step modal for vendor identity.
 * Styled to match LoginPage (Obsidian Bloom tokens, Dialog primitive).
 * In dev (NODE_ENV=development) the API returns demoCode — shown + prefilled.
 * After OTP verify, optionally captures name (skippable) via PATCH /vendor/me.
 * On success calls VendorModalCallbacks._onLoginSuccess so requireAuth() resolves.
 */
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { Dialog } from "@/components/ui/Dialog";
import { api, AppError } from "@/lib/api";
import { VendorModalCallbacks } from "./vendorModalCallbacks";
// ── FieldError ────────────────────────────────────────────────────────────────
function FieldError({ msg }) {
    if (!msg)
        return null;
    return _jsx("p", { className: "mt-1 text-xs text-status-danger", children: msg });
}
function PhoneStep({ onSuccess }) {
    const [rawPhone, setRawPhone] = useState("");
    const [error, setError] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const inputRef = useRef(null);
    useEffect(() => {
        const t = setTimeout(() => inputRef.current?.focus(), 80);
        return () => clearTimeout(t);
    }, []);
    const handleSubmit = useCallback(async (e) => {
        e.preventDefault();
        const digits = rawPhone.replace(/\D/g, "");
        if (digits.length < 10) {
            setError("Enter a valid 10-digit mobile number.");
            return;
        }
        setError(null);
        setSubmitting(true);
        const phone = `+91${digits.slice(-10)}`;
        try {
            const res = await api.post("/vendor/auth/request-otp", { phone });
            onSuccess(phone, res.demoCode);
        }
        catch (err) {
            setError(err instanceof AppError ? err.message : "Something went wrong. Please try again.");
        }
        finally {
            setSubmitting(false);
        }
    }, [rawPhone, onSuccess]);
    return (_jsxs("form", { onSubmit: (e) => void handleSubmit(e), className: "space-y-5", noValidate: true, children: [_jsxs("div", { className: "text-center space-y-1", children: [_jsx("h2", { className: "font-display italic text-2xl text-text", children: "Verify your number" }), _jsx("p", { className: "text-sm text-text-muted", children: "We'll send a one-time code so we know it's you." })] }), _jsxs("div", { children: [_jsx("label", { htmlFor: "vendor-phone", className: "block text-sm font-medium text-text mb-1", children: "Mobile number" }), _jsxs("div", { className: "flex", children: [_jsx("span", { className: "inline-flex items-center px-3 rounded-l-input border border-r-0 border-border-subtle\n              bg-surface-3 text-text-muted text-sm select-none", children: "+91" }), _jsx("input", { ref: inputRef, id: "vendor-phone", type: "tel", inputMode: "numeric", autoComplete: "tel", placeholder: "9876543210", "aria-label": "Mobile number", value: rawPhone, onChange: (e) => {
                                    setRawPhone(e.target.value);
                                    if (error)
                                        setError(null);
                                }, className: `flex-1 min-w-0 rounded-r-input border px-3 py-2.5 text-sm text-text
              placeholder:text-text-tertiary bg-surface-2 outline-none transition-all
              ${error
                                    ? "border-status-danger focus:ring-2 focus:ring-status-danger/30"
                                    : "border-border-subtle focus:border-[#9868cc] focus:ring-2 focus:ring-[#9868cc]/20"}` })] }), error && _jsx(FieldError, { msg: error })] }), _jsx("button", { type: "submit", disabled: submitting, className: "cta-pill w-full disabled:opacity-60 disabled:cursor-not-allowed", children: submitting ? "Sending OTP..." : "Send OTP" }), _jsx("p", { className: "text-xs text-text-tertiary text-center", children: "Your number identifies you across events. No spam, ever." })] }));
}
function OtpStep({ phone, demoCode, onBack, onSuccess }) {
    const [code, setCode] = useState(demoCode ?? "");
    const [error, setError] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const inputRef = useRef(null);
    useEffect(() => {
        const t = setTimeout(() => inputRef.current?.focus(), 80);
        return () => clearTimeout(t);
    }, []);
    const handleSubmit = useCallback(async (e) => {
        e.preventDefault();
        const trimmed = code.trim();
        if (trimmed.length < 4) {
            setError("Enter the full OTP code.");
            return;
        }
        setError(null);
        setSubmitting(true);
        try {
            const res = await api.post("/vendor/auth/verify-otp", { phone, code: trimmed });
            onSuccess(res.data.vendor);
        }
        catch (err) {
            setError(err instanceof AppError ? err.message : "Verification failed. Please try again.");
        }
        finally {
            setSubmitting(false);
        }
    }, [code, phone, onSuccess]);
    return (_jsxs("form", { onSubmit: (e) => void handleSubmit(e), className: "space-y-5", noValidate: true, children: [_jsxs("div", { className: "text-center space-y-1", children: [_jsx("h2", { className: "font-display italic text-2xl text-text", children: "Enter the code" }), _jsxs("p", { className: "text-sm text-text-muted", children: ["Sent to ", _jsx("span", { className: "text-text font-medium", children: phone })] })] }), demoCode && (_jsxs("div", { role: "status", "aria-live": "polite", className: "flex items-center gap-2 bg-status-info/10 border border-status-info/30\n            rounded-input px-3 py-2.5 text-sm text-status-info", children: [_jsxs("svg", { viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round", className: "w-4 h-4 shrink-0", "aria-hidden": "true", children: [_jsx("circle", { cx: "12", cy: "12", r: "10" }), _jsx("line", { x1: "12", y1: "8", x2: "12", y2: "12" }), _jsx("line", { x1: "12", y1: "16", x2: "12.01", y2: "16" })] }), "Dev mode \u2014 demo code:\u00A0", _jsx("span", { className: "font-mono font-bold tracking-widest", children: demoCode })] })), _jsxs("div", { children: [_jsx("label", { htmlFor: "vendor-otp", className: "block text-sm font-medium text-text mb-1", children: "6-digit OTP" }), _jsx("input", { ref: inputRef, id: "vendor-otp", type: "text", inputMode: "numeric", autoComplete: "one-time-code", maxLength: 6, placeholder: "123456", value: code, onChange: (e) => {
                            setCode(e.target.value);
                            if (error)
                                setError(null);
                        }, className: `w-full rounded-input border px-3 py-2.5 text-sm text-text
            placeholder:text-text-tertiary bg-surface-2 outline-none transition-all
            tracking-[0.3em] font-mono text-center
            ${error
                            ? "border-status-danger focus:ring-2 focus:ring-status-danger/30"
                            : "border-border-subtle focus:border-[#9868cc] focus:ring-2 focus:ring-[#9868cc]/20"}` }), error && _jsx(FieldError, { msg: error })] }), _jsx("button", { type: "submit", disabled: submitting, className: "cta-pill w-full disabled:opacity-60 disabled:cursor-not-allowed", children: submitting ? "Verifying..." : "Verify & continue" }), _jsx("button", { type: "button", onClick: onBack, className: "w-full text-sm text-text-muted hover:text-text transition-colors text-center", children: "Change number" })] }));
}
function NameStep({ onSave, onSkip: _onSkip }) {
    const [name, setName] = useState("");
    const [businessName, setBusinessName] = useState("");
    const [location, setLocation] = useState("");
    const [email, setEmail] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const trimmedName = name.trim();
    const trimmedBusiness = businessName.trim();
    const trimmedLocation = location.trim();
    const trimmedEmail = email.trim();
    const emailValid = trimmedEmail === "" || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail);
    const canSubmit = trimmedName.length >= 2 &&
        trimmedBusiness.length >= 2 &&
        emailValid &&
        !submitting;
    const handleSubmit = useCallback(async (e) => {
        e.preventDefault();
        if (trimmedName.length < 2) {
            setError("Please enter your name.");
            return;
        }
        if (trimmedBusiness.length < 2) {
            setError("Please enter your business name.");
            return;
        }
        if (!emailValid) {
            setError("That email doesn't look right.");
            return;
        }
        setSubmitting(true);
        try {
            await onSave({
                name: trimmedName,
                businessName: trimmedBusiness,
                ...(trimmedLocation ? { location: trimmedLocation } : {}),
                ...(trimmedEmail ? { email: trimmedEmail } : {}),
            });
        }
        catch {
            setError("Could not save. Please try again.");
        }
        finally {
            setSubmitting(false);
        }
    }, [trimmedName, trimmedBusiness, trimmedLocation, trimmedEmail, emailValid, onSave]);
    const inputCls = "w-full rounded-input border border-border-subtle px-3 py-2.5 text-sm text-text " +
        "placeholder:text-text-tertiary bg-surface-2 outline-none transition-all " +
        "focus:border-[#9868cc] focus:ring-2 focus:ring-[#9868cc]/20";
    return (_jsxs("form", { onSubmit: (e) => void handleSubmit(e), className: "space-y-4", noValidate: true, children: [_jsxs("div", { className: "text-center space-y-1", children: [_jsx("div", { className: "w-14 h-14 rounded-full bg-[#9868cc]/20 border border-[#9868cc]/30 flex items-center justify-center mx-auto mb-2", children: _jsx("svg", { viewBox: "0 0 24 24", fill: "none", stroke: "#9868cc", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round", className: "w-7 h-7", "aria-hidden": "true", children: _jsx("polyline", { points: "20 6 9 17 4 12" }) }) }), _jsx("h2", { className: "font-display italic text-2xl text-text", children: "You're verified!" }), _jsx("p", { className: "text-sm text-text-muted", children: "Quick profile so organizers know who's reaching out." })] }), _jsxs("div", { children: [_jsx("label", { htmlFor: "vendor-name", className: "block text-sm font-medium text-text mb-1", children: "Your name" }), _jsx("input", { id: "vendor-name", type: "text", autoComplete: "name", autoFocus: true, placeholder: "e.g. Priya Reddy", value: name, onChange: (e) => {
                            setName(e.target.value);
                            if (error)
                                setError(null);
                        }, className: inputCls })] }), _jsxs("div", { children: [_jsx("label", { htmlFor: "vendor-business", className: "block text-sm font-medium text-text mb-1", children: "Business name" }), _jsx("input", { id: "vendor-business", type: "text", autoComplete: "organization", placeholder: "e.g. Priya Jewellery", value: businessName, onChange: (e) => {
                            setBusinessName(e.target.value);
                            if (error)
                                setError(null);
                        }, className: inputCls })] }), _jsxs("div", { children: [_jsxs("label", { htmlFor: "vendor-location", className: "block text-sm font-medium text-text mb-1", children: ["Where are you based", " ", _jsx("span", { className: "text-text-tertiary font-normal", children: "(optional)" })] }), _jsx("input", { id: "vendor-location", type: "text", autoComplete: "address-level2", placeholder: "e.g. Banjara Hills, Hyderabad", value: location, onChange: (e) => {
                            setLocation(e.target.value);
                            if (error)
                                setError(null);
                        }, className: inputCls })] }), _jsxs("div", { children: [_jsxs("label", { htmlFor: "vendor-email", className: "block text-sm font-medium text-text mb-1", children: ["Email", " ", _jsx("span", { className: "text-text-tertiary font-normal", children: "(optional)" })] }), _jsx("input", { id: "vendor-email", type: "email", autoComplete: "email", placeholder: "you@example.com", value: email, onChange: (e) => {
                            setEmail(e.target.value);
                            if (error)
                                setError(null);
                        }, className: inputCls }), error && _jsx(FieldError, { msg: error })] }), _jsx("button", { type: "submit", disabled: !canSubmit, className: "cta-pill w-full disabled:opacity-60 disabled:cursor-not-allowed", children: submitting ? "Saving..." : "Continue" })] }));
}
export function VendorAuthModal({ open, onOpenChange }) {
    const callbacks = useContext(VendorModalCallbacks);
    const [step, setStep] = useState("phone");
    const [phone, setPhone] = useState("");
    const [demoCode, setDemoCode] = useState(undefined);
    const [pendingVendor, setPendingVendor] = useState(null);
    // Reset when modal closes.
    useEffect(() => {
        if (!open) {
            const t = setTimeout(() => {
                setStep("phone");
                setPhone("");
                setDemoCode(undefined);
                setPendingVendor(null);
            }, 300);
            return () => clearTimeout(t);
        }
        return undefined;
    }, [open]);
    function handlePhoneSuccess(resolvedPhone, code) {
        setPhone(resolvedPhone);
        setDemoCode(code);
        setStep("otp");
    }
    function handleOtpSuccess(vendor) {
        // Show the profile step on first login — until at minimum the required
        // fields (name + businessName) are filled.
        if (!vendor.name || !vendor.businessName) {
            setPendingVendor(vendor);
            setStep("name");
        }
        else {
            callbacks?._onLoginSuccess(vendor);
        }
    }
    async function handleNameSave(fields) {
        if (!pendingVendor)
            return;
        const res = await api.patch("/vendor/me", fields);
        callbacks?._onLoginSuccess(res.data.vendor);
    }
    function handleNameSkip() {
        if (pendingVendor) {
            callbacks?._onLoginSuccess(pendingVendor);
        }
    }
    // Block dialog dismissal during the name step — the OTP cookie is already
    // set at this point, so closing without a name would leave a nameless
    // vendor past the gate.
    const lockClose = step === "name";
    return (_jsx(Dialog, { open: open, onOpenChange: (next) => {
            if (!next && lockClose)
                return;
            onOpenChange(next);
        }, onInteractOutside: lockClose ? (e) => e.preventDefault() : undefined, children: _jsxs("div", { className: "pt-2", children: [step === "phone" && _jsx(PhoneStep, { onSuccess: handlePhoneSuccess }), step === "otp" && (_jsx(OtpStep, { phone: phone, demoCode: demoCode, onBack: () => setStep("phone"), onSuccess: handleOtpSuccess })), step === "name" && (_jsx(NameStep, { onSave: handleNameSave, onSkip: handleNameSkip }))] }) }));
}
